import { Vector4 } from "../../maths/Vector4";
import { Earcut } from "../shapes/Earcut";
const _ClipTriangle = [
    { x: 0, y: 0, u: 0, v: 0, r: 0, g: 0, b: 0, a: 0, index: 0 },
    { x: 0, y: 0, u: 0, v: 0, r: 0, g: 0, b: 0, a: 0, index: 0 },
    { x: 0, y: 0, u: 0, v: 0, r: 0, g: 0, b: 0, a: 0, index: 0 }
];
const tempVec4 = new Vector4();
export class UVClippingUtils {
    static _resetBuffers() {
        const ctx = UVClippingUtils._threadLocalContext;
        ctx.outVerticesLength = 0;
        ctx.outIndicesLength = 0;
        ctx.outUVsLength = 0;
        ctx.outColorsLength = 0;
        ctx.clipBuffer1Length = 0;
        ctx.clipBuffer2Length = 0;
    }
    static _ensureBufferCapacity(buffer, requiredLength, constructor) {
        if (buffer.length >= requiredLength) {
            return buffer;
        }
        const newLength = Math.max(requiredLength, Math.ceil(buffer.length * 1.5));
        return new constructor(newLength);
    }
    static clipTrianglesByUVRange(vertices, indices, uvs, uvRange, colors, reuseBuffer = true) {
        UVClippingUtils._resetBuffers();
        const ctx = UVClippingUtils._threadLocalContext;
        const EPSILON = UVClippingUtils.EPSILON;
        const minU = uvRange[0];
        const minV = uvRange[1];
        const maxU = uvRange[0] + uvRange[2];
        const maxV = uvRange[1] + uvRange[3];
        let epsilon_edge = tempVec4;
        epsilon_edge.x = minU - EPSILON;
        epsilon_edge.y = minV - EPSILON;
        epsilon_edge.z = maxU + EPSILON;
        epsilon_edge.w = maxV + EPSILON;
        const vertexCount = uvs.length / 2;
        const vertexInsideFlags = new Uint8Array(vertexCount);
        let allUVsInside = true;
        for (let i = 0; i < vertexCount; i++) {
            const u = uvs[i * 2];
            const v = uvs[i * 2 + 1];
            let flags = 0;
            if (u >= epsilon_edge.x)
                flags |= 0x01;
            if (u <= epsilon_edge.z)
                flags |= 0x02;
            if (v >= epsilon_edge.y)
                flags |= 0x04;
            if (v <= epsilon_edge.w)
                flags |= 0x08;
            vertexInsideFlags[i] = flags;
            if (flags !== 0x0F) {
                allUVsInside = false;
            }
        }
        if (allUVsInside) {
            return {
                vertices,
                indices,
                uvs,
                colors,
            };
        }
        const outVertices = ctx.outVertices;
        const outIndices = ctx.outIndices;
        const outUVs = ctx.outUVs;
        const outColors = ctx.outColors;
        let nextVertexIndex = 0;
        const addVertex = (vert) => {
            const index = nextVertexIndex++;
            const baseIdx = ctx.outVerticesLength;
            const colorIdx = ctx.outColorsLength;
            outVertices[baseIdx] = vert.x;
            outVertices[baseIdx + 1] = vert.y;
            outUVs[baseIdx] = vert.u;
            outUVs[baseIdx + 1] = vert.v;
            outColors[colorIdx] = vert.r;
            outColors[colorIdx + 1] = vert.g;
            outColors[colorIdx + 2] = vert.b;
            outColors[colorIdx + 3] = vert.a;
            ctx.outVerticesLength = baseIdx + 2;
            ctx.outUVsLength = baseIdx + 2;
            ctx.outColorsLength = colorIdx + 4;
            return index;
        };
        const isInside = (vert, edge) => {
            switch (edge) {
                case 0: return vert.u >= epsilon_edge.x;
                case 1: return vert.u <= epsilon_edge.z;
                case 2: return vert.v >= epsilon_edge.y;
                case 3: return vert.v <= epsilon_edge.w;
            }
            return false;
        };
        const setClipVertex = (buffer, id, x, y, u, v, r, g, b, a, index) => {
            if (!buffer[id]) {
                buffer[id] = { x, y, u, v, r, g, b, a, index };
            }
            else {
                let vertex = buffer[id];
                vertex.x = x;
                vertex.y = y;
                vertex.u = u;
                vertex.v = v;
                vertex.r = r;
                vertex.g = g;
                vertex.b = b;
                vertex.a = a;
                vertex.index = index;
            }
        };
        const computeIntersection = (v1, v2, edge, output, outputLen) => {
            let t;
            switch (edge) {
                case 0:
                    if (Math.abs(v2.u - v1.u) < EPSILON)
                        return false;
                    t = (minU - v1.u) / (v2.u - v1.u);
                    break;
                case 1:
                    if (Math.abs(v2.u - v1.u) < EPSILON)
                        return false;
                    t = (maxU - v1.u) / (v2.u - v1.u);
                    break;
                case 2:
                    if (Math.abs(v2.v - v1.v) < EPSILON)
                        return false;
                    t = (minV - v1.v) / (v2.v - v1.v);
                    break;
                case 3:
                    if (Math.abs(v2.v - v1.v) < EPSILON)
                        return false;
                    t = (maxV - v1.v) / (v2.v - v1.v);
                    break;
                default:
                    return false;
            }
            if (t < -EPSILON || t > 1 + EPSILON)
                return false;
            setClipVertex(output, outputLen, v1.x + t * (v2.x - v1.x), v1.y + t * (v2.y - v1.y), v1.u + t * (v2.u - v1.u), v1.v + t * (v2.v - v1.v), v1.r + t * (v2.r - v1.r), v1.g + t * (v2.g - v1.g), v1.b + t * (v2.b - v1.b), v1.a + t * (v2.a - v1.a), -1);
            return true;
        };
        const clipPolygon = (inputVertices, inputLength) => {
            let input = inputVertices;
            let inputLen = inputLength;
            let outputBuffer = ctx.clipBuffer1;
            for (let edge = 0; edge < 4; edge++) {
                const output = (edge % 2 === 0) ? ctx.clipBuffer1 : ctx.clipBuffer2;
                let outputLen = 0;
                if (inputLen === 0)
                    return { length: 0, buffer: output };
                const edgeMask = 1 << edge;
                for (let i = 0; i < inputLen; i++) {
                    const current = input[i];
                    const next = input[(i + 1) % inputLen];
                    const currentInside = current.index >= 0
                        ? (vertexInsideFlags[current.index] & edgeMask) !== 0
                        : isInside(current, edge);
                    const nextInside = next.index >= 0
                        ? (vertexInsideFlags[next.index] & edgeMask) !== 0
                        : isInside(next, edge);
                    if (currentInside) {
                        setClipVertex(output, outputLen++, current.x, current.y, current.u, current.v, current.r, current.g, current.b, current.a, current.index);
                        if (!nextInside) {
                            if (computeIntersection(current, next, edge, output, outputLen)) {
                                outputLen++;
                            }
                        }
                    }
                    else if (nextInside) {
                        if (computeIntersection(current, next, edge, output, outputLen)) {
                            outputLen++;
                        }
                    }
                }
                if (outputLen === 0)
                    return { length: 0, buffer: output };
                input = output;
                inputLen = outputLen;
                outputBuffer = output;
                if (edge % 2 === 0) {
                    ctx.clipBuffer1Length = outputLen;
                }
                else {
                    ctx.clipBuffer2Length = outputLen;
                }
            }
            return { length: inputLen, buffer: outputBuffer };
        };
        const buildVertex = (index, vertexIndex) => {
            let vertex = _ClipTriangle[index];
            vertex.x = vertices[vertexIndex * 2];
            vertex.y = vertices[vertexIndex * 2 + 1];
            vertex.u = uvs[vertexIndex * 2];
            vertex.v = uvs[vertexIndex * 2 + 1];
            vertex.r = colors[vertexIndex * 4];
            vertex.g = colors[vertexIndex * 4 + 1];
            vertex.b = colors[vertexIndex * 4 + 2];
            vertex.a = colors[vertexIndex * 4 + 3];
            vertex.index = vertexIndex;
            return vertex;
        };
        const triangleCount = indices.length / 3;
        let triangle = _ClipTriangle;
        for (let t = 0; t < triangleCount; t++) {
            buildVertex(0, indices[t * 3]);
            buildVertex(1, indices[t * 3 + 1]);
            buildVertex(2, indices[t * 3 + 2]);
            const flags0 = vertexInsideFlags[triangle[0].index];
            const flags1 = vertexInsideFlags[triangle[1].index];
            const flags2 = vertexInsideFlags[triangle[2].index];
            if (flags0 === 0x0F && flags1 === 0x0F && flags2 === 0x0F) {
                const idx0 = addVertex(triangle[0]);
                const idx1 = addVertex(triangle[1]);
                const idx2 = addVertex(triangle[2]);
                outIndices[ctx.outIndicesLength++] = idx0;
                outIndices[ctx.outIndicesLength++] = idx1;
                outIndices[ctx.outIndicesLength++] = idx2;
                continue;
            }
            const combinedFlags = flags0 | flags1 | flags2;
            if (!(combinedFlags & 0x01) ||
                !(combinedFlags & 0x02) ||
                !(combinedFlags & 0x04) ||
                !(combinedFlags & 0x08)) {
                continue;
            }
            const clippedResult = clipPolygon(triangle, 3);
            const clippedPolygonLen = clippedResult.length;
            const clippedPolygon = clippedResult.buffer;
            if (clippedPolygonLen < 3) {
                continue;
            }
            if (clippedPolygonLen === 3) {
                const idx0 = addVertex(clippedPolygon[0]);
                const idx1 = addVertex(clippedPolygon[1]);
                const idx2 = addVertex(clippedPolygon[2]);
                outIndices[ctx.outIndicesLength++] = idx0;
                outIndices[ctx.outIndicesLength++] = idx1;
                outIndices[ctx.outIndicesLength++] = idx2;
            }
            else {
                const earcutData = ctx.earcutData;
                let earcutLen = 0;
                for (let i = 0; i < clippedPolygonLen; i++) {
                    earcutData[earcutLen++] = clippedPolygon[i].x;
                    earcutData[earcutLen++] = clippedPolygon[i].y;
                }
                earcutData.length = earcutLen;
                const triangulated = Earcut.earcut(earcutData, null, 2);
                if (triangulated.length > 0) {
                    const vertexIndices = ctx.vertexIndices;
                    for (let i = 0; i < clippedPolygonLen; i++) {
                        vertexIndices[i] = addVertex(clippedPolygon[i]);
                    }
                    for (let i = 0; i < triangulated.length; i += 3) {
                        outIndices[ctx.outIndicesLength++] = vertexIndices[triangulated[i]];
                        outIndices[ctx.outIndicesLength++] = vertexIndices[triangulated[i + 1]];
                        outIndices[ctx.outIndicesLength++] = vertexIndices[triangulated[i + 2]];
                    }
                }
            }
        }
        if (reuseBuffer) {
            ctx.verticesBuffer = UVClippingUtils._ensureBufferCapacity(ctx.verticesBuffer, ctx.outVerticesLength, Float32Array);
            ctx.indicesBuffer = UVClippingUtils._ensureBufferCapacity(ctx.indicesBuffer, ctx.outIndicesLength, Uint16Array);
            ctx.uvsBuffer = UVClippingUtils._ensureBufferCapacity(ctx.uvsBuffer, ctx.outUVsLength, Float32Array);
            ctx.colorsBuffer = UVClippingUtils._ensureBufferCapacity(ctx.colorsBuffer, ctx.outColorsLength, Float32Array);
            for (let i = 0; i < ctx.outVerticesLength; i++) {
                ctx.verticesBuffer[i] = outVertices[i];
            }
            for (let i = 0; i < ctx.outIndicesLength; i++) {
                ctx.indicesBuffer[i] = outIndices[i];
            }
            for (let i = 0; i < ctx.outUVsLength; i++) {
                ctx.uvsBuffer[i] = outUVs[i];
            }
            for (let i = 0; i < ctx.outColorsLength; i++) {
                ctx.colorsBuffer[i] = outColors[i];
            }
            return {
                vertices: ctx.verticesBuffer.subarray(0, ctx.outVerticesLength),
                indices: ctx.indicesBuffer.subarray(0, ctx.outIndicesLength),
                uvs: ctx.uvsBuffer.subarray(0, ctx.outUVsLength),
                colors: ctx.colorsBuffer.subarray(0, ctx.outColorsLength)
            };
        }
        else {
            const newVertices = new Float32Array(ctx.outVerticesLength);
            const newIndices = new Uint16Array(ctx.outIndicesLength);
            const newUVs = new Float32Array(ctx.outUVsLength);
            const newColors = new Float32Array(ctx.outColorsLength);
            for (let i = 0; i < ctx.outVerticesLength; i++) {
                newVertices[i] = outVertices[i];
            }
            for (let i = 0; i < ctx.outIndicesLength; i++) {
                newIndices[i] = outIndices[i];
            }
            for (let i = 0; i < ctx.outUVsLength; i++) {
                newUVs[i] = outUVs[i];
            }
            for (let i = 0; i < ctx.outColorsLength; i++) {
                newColors[i] = outColors[i];
            }
            return {
                vertices: newVertices,
                indices: newIndices,
                uvs: newUVs,
                colors: newColors
            };
        }
    }
}
UVClippingUtils.EPSILON = 1e-7;
UVClippingUtils._threadLocalContext = {
    outVertices: [],
    outIndices: [],
    outUVs: [],
    outColors: [],
    clipBuffer1: [],
    clipBuffer2: [],
    earcutData: [],
    vertexIndices: [],
    outVerticesLength: 0,
    outIndicesLength: 0,
    outUVsLength: 0,
    outColorsLength: 0,
    clipBuffer1Length: 0,
    clipBuffer2Length: 0,
    verticesBuffer: new Float32Array(0),
    indicesBuffer: new Uint16Array(0),
    uvsBuffer: new Float32Array(0),
    colorsBuffer: new Float32Array(0),
};
